import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertBanner, Button } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints, type FindingAttachment } from '../state/api.js';
import { useSession } from '../state/session.js';

/**
 * Foto temuan — MENAMPILKAN dan MENGUNGGAH.
 *
 * DUA DEFECT UAT G3 YANG ADA DI TEMPAT INI, DAN KEDUANYA MEMBUAT SATU ALUR MATI
 * ============================================================================
 *
 * **U2-07 (High) — foto tidak pernah ditampilkan; ULID mentah dipakai sebagai
 * keterangan.** `FindingDetail.tsx` merender `<div className="photo__frame"
 * aria-hidden="true" />` — sebuah bingkai kosong — dan `figcaption` yang jatuh ke
 * `a.file_object_id`, sehingga yang terbaca di layar adalah
 * `01M3F8B3WZBV0YA3E4FQNQZ9BM`. Nol elemen `<img>` di seluruh konsol.
 * `GET /v1/files/{id}/download-url` berfungsi dan tidak dipakai.
 *
 * Akibatnya bukan kosmetik: alur 2 §6 menuntut manajer MEMBANDINGKAN foto sebelum dan
 * sesudah untuk memverifikasi perbaikan. Tanpa fotonya, verifikasi adalah tanda tangan
 * di atas sesuatu yang tidak pernah dilihat.
 *
 * **LDR-4 / U2-03 — NOL `input[type=file]` di seluruh konsol.** Leader area tidak punya
 * jalan melampirkan foto "sesudah", sehingga tombol "Selesaikan temuan" tetap mati
 * dengan alasan yang benar (*"Foto 'sesudah' wajib ada sebelum ini dapat ditutup.
 * Terlampir 0 dari 1."*) dan tidak ada apa pun yang dapat dilakukan orangnya. Alur 2
 * berhasil lewat API dan GAGAL dari sisi pengguna.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * KENAPA UNGGAHNYA TIGA PERMINTAAN, DAN KENAPA ITU BUKAN KERUMITAN YANG DITAMBAHKAN
 * ═══════════════════════════════════════════════════════════════════════════════
 * Berkas TIDAK melewati API (ADR-8): perangkat menerima URL bertanda tangan dan
 * mengunggah LANGSUNG ke penyimpanan objek. Jadi satu foto adalah:
 *
 *   1. `POST /v1/files/upload-intents` → `file_id` + `upload_url` + batas yang DIIKAT
 *      tanda tangan URL-nya;
 *   2. `PUT upload_url` dengan byte-nya — ke penyimpanan objek, bukan ke API;
 *   3. `POST /v1/files/{id}/commit` dengan ukuran dan checksum SHA-256 yang dihitung
 *      di peramban;
 *   4. lalu melampirkannya, dan server MENUNGGU verifikasinya sampai empat detik
 *      (U2-01b) sehingga orangnya tidak perlu menekan tombolnya dua kali.
 *
 * Checksum-nya dihitung dengan `crypto.subtle.digest` — API peramban bawaan, tanpa
 * dependensi. Ia WAJIB: `commit` menolak tanpa checksum, dan pekerja verifikasi
 * membandingkannya dengan objek yang BENAR-BENAR tersimpan (butir G). Menghitungnya di
 * klien bukan kontrol keamanan — klien tidak dipercaya — melainkan cara mendeteksi
 * unggahan yang rusak di tengah jalan dan objek yang ditimpa sesudah commit.
 *
 * BATAS UKURAN DITEGAKKAN DI KLIEN JUGA, DAN ITU HANYA UNTUK UX.
 * `max_byte_size` dari jawaban langkah 1 dibandingkan sebelum `PUT`, supaya orangnya
 * tahu sebelum menunggu unggahan yang pasti ditolak. Yang menegakkannya sungguhan
 * adalah tanda tangan URL-nya, CHECK constraint di basis data, dan pemeriksa byte di
 * pekerja — tiga lapis yang tidak menanyakan pendapat klien.
 */

const SHA256_UNAVAILABLE = 'SHA256_UNAVAILABLE';

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  /**
   * `crypto.subtle` hanya ada di konteks aman (https, atau localhost). Bila ia tidak
   * ada, unggahan TIDAK dicoba: `commit` menuntut checksum heksadesimal 64 karakter,
   * dan mengirim checksum yang salah akan membuat pekerja MENGARANTINA fotonya dengan
   * alasan `CHECKSUM_MISMATCH` — yaitu menyalahkan berkas atas kekurangan peramban.
   */
  if (!globalThis.crypto?.subtle) throw new Error(SHA256_UNAVAILABLE);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface PhotoAttachmentsProps {
  code: string;
  attachments: FindingAttachment[];
  /** Peran yang dapat diunggah dari layar ini. Detail temuan memakai BEFORE dan AFTER. */
  roles: ReadonlyArray<'BEFORE' | 'AFTER' | 'EVIDENCE'>;
  /** Benar bila pengguna ini boleh melampirkan. Server tetap yang memutuskan. */
  canAttach: boolean;
  onChanged: () => void | Promise<void>;
}

export function PhotoAttachments({
  code,
  attachments,
  roles,
  canAttach,
  onChanged,
}: PhotoAttachmentsProps) {
  const { t, translator } = useSession();
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busyRole, setBusyRole] = useState<string | null>(null);
  const [phase, setPhase] = useState<'uploading' | 'checking' | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [failedLocal, setFailedLocal] = useState<string | null>(null);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  /**
   * URL unduh berumur pendek (90 detik), diminta per lampiran.
   *
   * Ia TIDAK di-cache melewati pemuatan layar dengan sengaja: umur pendek itu adalah
   * mitigasi yang dinyatakan untuk `object_key` yang membawa ULID organisasi
   * (ISOLASI-ANTAR-ORGANISASI.md §4.8), dan menyimpannya lebih lama membatalkan
   * mitigasinya. Objek yang BELUM diverifikasi dijawab `409 PHOTO_METADATA_PENDING`,
   * dan itu keadaan sementara yang dikatakan apa adanya di layar — bukan gambar rusak.
   */
  const loadUrls = useCallback(async () => {
    const hasil: Record<string, string> = {};
    for (const a of attachments) {
      try {
        const res = await endpoints.downloadUrl(a.file_object_id);
        hasil[a.file_object_id] = res.download_url;
      } catch {
        // Foto yang belum siap tidak punya URL, dan itu bukan galat layar.
      }
    }
    setUrls(hasil);
  }, [attachments]);

  useEffect(() => {
    void loadUrls();
  }, [loadUrls]);

  async function unggah(role: 'BEFORE' | 'AFTER' | 'EVIDENCE', file: File) {
    setProblem(null);
    setFailedLocal(null);
    setBusyRole(role);
    try {
      const intent = await endpoints.uploadIntent({
        purpose: 'ITEM_PHOTO',
        mime_type: file.type as 'image/jpeg',
        byte_size: file.size,
        filename: file.name,
      });
      if (intent.max_byte_size && file.size > intent.max_byte_size) {
        // Ditolak SEBELUM mengunggah: orangnya tidak perlu menunggu unggahan yang
        // pasti ditolak. Kalimatnya kalimat yang sama dengan yang dikirim server,
        // dari katalog yang sama.
        setProblem(
          new ApiError(
            {
              code: 'PHOTO_TOO_LARGE',
              status: 422,
              params: {
                max_byte_size: intent.max_byte_size,
                max_pixel_edge: intent.max_pixel_edge ?? 1600,
              },
            },
            422,
          ),
        );
        return;
      }
      const bytes = await file.arrayBuffer();
      const checksum = await sha256Hex(bytes);

      setPhase('uploading');
      const put = await fetch(intent.upload_url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: bytes,
      });
      if (!put.ok) {
        setFailedLocal('UPLOAD_FAILED');
        return;
      }

      setPhase('checking');
      await endpoints.commitFile(intent.file_id, { byte_size: file.size, checksum_sha256: checksum });
      await endpoints.attachFile(code, { file_object_id: intent.file_id, role });
      await onChanged();
      await loadUrls();
    } catch (err) {
      if (err instanceof Error && err.message === SHA256_UNAVAILABLE) {
        setFailedLocal(SHA256_UNAVAILABLE);
      } else if (err instanceof ApiError) {
        setProblem(err);
      } else {
        setFailedLocal('UPLOAD_FAILED');
      }
    } finally {
      setBusyRole(null);
      setPhase(null);
    }
  }

  return (
    <section className="card">
      <h2 className="card__title">{t('finding.photos')}</h2>

      {problem ? (
        <AlertBanner
          kind="danger"
          signalWord={t('signal.danger')}
          title={renderProblem(translator, problem.problem)}
        />
      ) : null}
      {failedLocal ? (
        <AlertBanner
          kind="warning"
          signalWord={t('signal.warning')}
          title={t('finding.photo.unavailable')}
        />
      ) : null}

      <div className="photos">
        {roles.map((role) => {
          const milik = attachments.filter((a) => a.role === role);
          return (
            <div key={role} className="photos__col">
              <h3>
                {role === 'BEFORE'
                  ? t('finding.photos.before')
                  : role === 'AFTER'
                    ? t('finding.photos.after')
                    : t('finding.photos.evidence')}
              </h3>

              {milik.length === 0 ? <p className="card__muted">—</p> : null}

              {milik.map((a) => {
                const url = urls[a.file_object_id];
                const labelPeran =
                  role === 'BEFORE'
                    ? t('finding.photos.before')
                    : role === 'AFTER'
                      ? t('finding.photos.after')
                      : t('finding.photos.evidence');
                return (
                  <figure key={a.id} className="photo">
                    {url ? (
                      /*
                        ELEMEN `<img>` YANG SUNGGUHAN (U2-07). `alt` menyebut peran dan
                        kode temuannya — bukan ULID-nya: sebuah keterangan yang berbunyi
                        `01M3F8B3WZBV0YA3E4FQNQZ9BM` tidak memberi tahu pembaca layar
                        apa pun, dan itulah yang tampil sebelum perbaikan ini.
                      */
                      <img
                        className="photo__img"
                        src={url}
                        alt={t('finding.photo.alt', { role: labelPeran, code })}
                        loading="lazy"
                      />
                    ) : (
                      <p className="card__muted">{t('finding.photo.unavailable')}</p>
                    )}
                    {a.caption ? <figcaption>{a.caption}</figcaption> : null}
                  </figure>
                );
              })}

              {canAttach ? (
                <p className="photos__action">
                  {/*
                    KENDALI UNGGAH YANG SUNGGUHAN — `input[type=file]`, yang sebelumnya
                    NOL di seluruh konsol (LDR-4).

                    `capture="environment"` membuat peramban ponsel membuka kamera
                    belakang langsung, dan itu bukan kenyamanan: foto yang diambil dari
                    kamera aplikasi sudah dikecilkan peramban, sementara foto yang
                    dipilih dari galeri hampir selalu melewati 300 KB — yaitu kegagalan
                    yang paling sering terjadi (U2-08).
                  */}
                  <input
                    ref={(el) => {
                      inputs.current[role] = el;
                    }}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    className="photos__input"
                    aria-label={t('finding.photo.choose')}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void unggah(role, file);
                    }}
                  />
                  <Button
                    variant="secondary"
                    busy={busyRole === role}
                    onClick={() => inputs.current[role]?.click()}
                  >
                    {busyRole === role
                      ? phase === 'checking'
                        ? t('finding.photo.checking')
                        : t('finding.photo.uploading')
                      : t('finding.photo.add')}
                  </Button>
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
